import { apiFetch, apiFetchText } from './api'
import type { OrderStatus, PolicyStatus } from './customers-api'

/** Mirrors apps/api/src/backoffice/orders.dto.ts and payments.dto.ts. Read-only. */

export interface CustomerRefDto {
  id: string
  mobile: string
  firstName: string | null
  lastName: string | null
}

export interface OrderListItemDto {
  id: string
  status: OrderStatus
  statusFa: string
  amount: number
  productTitleFa: string
  insurerName: string
  customer: CustomerRefDto
  plateFa: string | null
  policy: { id: string; policyNumber: string } | null
  /** Paid (PAID, ISSUING or ISSUE_FAILED) and still no policy. */
  paidWithoutPolicy: boolean
  paymentCount: number
  createdAt: string
  updatedAt: string
}

export type PaymentStatus = 'CREATED' | 'REDIRECTED' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED'

export interface PaymentListItemDto {
  id: string
  status: PaymentStatus
  statusFa: string
  amount: number
  gateway: string
  authority: string
  refId: string | null
  cardMask: string | null
  requestedAt: string
  verifiedAt: string | null
  order: { id: string; status: OrderStatus; statusFa: string }
  customer: CustomerRefDto
}

export interface PageDto<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

/** Same labels the API sends as `statusFa`; needed here for the filter dropdowns. */
export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  DRAFT: 'پیش‌نویس',
  PENDING_PAYMENT: 'در انتظار پرداخت',
  PAYMENT_FAILED: 'پرداخت ناموفق',
  PAID: 'پرداخت‌شده',
  ISSUING: 'در حال صدور',
  ISSUED: 'صادر شده',
  ISSUE_FAILED: 'خطا در صدور',
  CANCELLED: 'لغو شده',
}

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  CREATED: 'ایجاد شده',
  REDIRECTED: 'در درگاه',
  SUCCEEDED: 'موفق',
  FAILED: 'ناموفق',
  CANCELLED: 'لغو شده',
}

/** Iran has had no daylight saving since 2022, so a day there is always +03:30. */
const tehranDay = (day: string, end: boolean) =>
  `${day}T${end ? '23:59:59.999' : '00:00:00.000'}+03:30`

/**
 * The page's URL params, passed through as the API's. Days become whole Tehran days and Toman
 * amounts become Rial; everything else is sent as it is in the address bar.
 */
const toQuery = (filters: URLSearchParams, toman: string[] = []): string => {
  const out = new URLSearchParams()
  for (const [key, value] of filters) {
    if (!value) continue
    if (key === 'from' || key === 'to') out.set(key, tehranDay(value, key === 'to'))
    else if (toman.includes(key)) out.set(key, String(Number(value) * 10))
    else out.set(key, value)
  }
  return out.toString()
}

export const listOrders = (filters: URLSearchParams) =>
  apiFetch<PageDto<OrderListItemDto>>(`/admin/orders?${toQuery(filters)}`)

/** `minAmount` / `maxAmount` in the URL are Toman, like everything a person reads or types. */
export const listPayments = (filters: URLSearchParams) =>
  apiFetch<PageDto<PaymentListItemDto>>(
    `/admin/payments?${toQuery(filters, ['minAmount', 'maxAmount'])}`,
  )

export type TimelineTone = 'good' | 'bad' | 'warn' | 'neutral'

/** Mirrors apps/api/src/backoffice/order-timeline.ts: row timestamps plus the order's event log. */
export interface TimelineEventDto {
  kind: string
  at: string
  titleFa: string
  detailFa: string | null
  tone: TimelineTone
  future: boolean
  /** Only on orders older than the event log: no timestamp of its own, shown at the last change. */
  approximate: boolean
  paymentId: string | null
}

export interface LabelValue {
  key?: string
  labelFa: string
  valueFa: string
}

export interface LineItem {
  key: string
  labelFa: string
  amount: number
  kind: 'PREMIUM' | 'DISCOUNT' | 'TAX' | 'FEE'
}

export interface OrderPaymentDto {
  id: string
  status: PaymentStatus
  statusFa: string
  amount: number
  gateway: string
  authority: string
  refId: string | null
  cardMask: string | null
  requestedAt: string
  verifiedAt: string | null
}

export interface OrderDetailDto {
  id: string
  status: OrderStatus
  statusFa: string
  amount: number
  paidWithoutPolicy: boolean
  createdAt: string
  updatedAt: string
  expiresAt: string | null
  customer: CustomerRefDto
  insured: {
    firstName?: string
    lastName?: string
    nationalCode?: string
    birthDate?: string
    passportNo?: string
  }[]
  quote: {
    id: string
    status: 'ACTIVE' | 'EXPIRED' | 'CONVERTED'
    createdAt: string
    expiresAt: string
    isSampleRates: boolean
    productTitleFa: string
    productType: 'TRAVEL' | 'MOTOR_TPL' | 'HOME_FIRE'
    plateFa: string | null
  }
  offer: {
    id: string
    insurerName: string
    netPremium: number
    totalAmount: number
    rateTableVersion: number
    lineItems: LineItem[]
    coverages: LabelValue[]
  }
  payments: OrderPaymentDto[]
  policy: {
    id: string
    policyNumber: string
    status: PolicyStatus
    statusFa: string
    startsAt: string
    endsAt: string
    issuedAt: string
    productTitleFa: string
    insurerName: string
    totalAmount: number | null
    insuredCount: number
    risk: LabelValue[]
    documentUrl: string
  } | null
  timeline: TimelineEventDto[]
}

export const getOrder = (id: string) =>
  apiFetch<OrderDetailDto>(`/admin/orders/${encodeURIComponent(id)}`)

/** `documentUrl` from the order detail; HTML, fetched with the staff token. */
export const getPolicyDocument = (documentUrl: string) => apiFetchText(documentUrl)
